import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, FormControlLabel, MenuItem, Paper, Switch, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportLcService } from '../../services/exportLc.service';
import { PERMISSIONS } from '../../constants/permissions';
import { DUE_STATUS_COLORS, ExportLcListItem, LC_STATUS_COLORS, LcStatus } from '../../interfaces/commercial.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';

const LC_STATUSES: LcStatus[] = ['RECEIVED', 'PARTIALLY_SHIPPED', 'FULLY_SHIPPED', 'REALIZED', 'CLOSED'];

const money = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ExportLcList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);

    const [rows, setRows] = useState<ExportLcListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<'ALL' | LcStatus>('ALL');
    const [bank, setBank] = useState('');
    const [buyer, setBuyer] = useState('');
    const [dueSoon, setDueSoon] = useState(false);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await ExportLcService.list(institutionId, {
                page, size,
                status: status === 'ALL' ? undefined : status,
                bank: bank.trim() || undefined,
                buyer: buyer.trim() || undefined,
                dueSoon: dueSoon || undefined,
            });
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load export LCs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, status, bank, buyer, dueSoon]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.lcList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Export LCs</Typography>
                {canManage && (
                    <Button data-help="commercial.lcList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/lc/new')}>
                        New LC
                    </Button>
                )}
            </Box>

            <Box data-help-section="commercial.lcList.filters" sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    data-help="commercial.lcList.statusFilter" select label="Status" size="small" value={status}
                    onChange={(e) => { setStatus(e.target.value as 'ALL' | LcStatus); setPage(0); }}
                    sx={{ flex: '1 1 180px', maxWidth: 300 }}
                >
                    <MenuItem value="ALL">All</MenuItem>
                    {LC_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                </TextField>
                <TextField
                    data-help="commercial.lcList.bank" label="Bank" size="small" value={bank} sx={{ flex: '1 1 180px', maxWidth: 280 }}
                    onChange={(e) => { setBank(e.target.value); setPage(0); }}
                />
                <TextField
                    data-help="commercial.lcList.buyer" label="Buyer" size="small" value={buyer} sx={{ flex: '1 1 180px', maxWidth: 280 }}
                    onChange={(e) => { setBuyer(e.target.value); setPage(0); }}
                />
                <FormControlLabel
                    data-help="commercial.lcList.dueSoon"
                    control={<Switch checked={dueSoon} onChange={(e) => { setDueSoon(e.target.checked); setPage(0); }} />}
                    label="Due soon"
                />
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.lcList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.lcList.col.number">LC Number</TableCell>
                                <TableCell data-help="commercial.lcList.col.bank">Bank</TableCell>
                                <TableCell data-help="commercial.lcList.col.buyer">Buyer</TableCell>
                                <TableCell align="right" data-help="commercial.lcList.col.amount">Amount</TableCell>
                                <TableCell align="right" data-help="commercial.lcList.col.effectiveMax">Effective Max</TableCell>
                                <TableCell data-help="commercial.lcList.col.expiry">Expiry</TableCell>
                                <TableCell data-help="commercial.lcList.col.status">Status</TableCell>
                                <TableCell data-help="commercial.lcList.col.due">Due</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/lc/${row.id}`)}>
                                    <TableCell>
                                        {row.lcNumber}
                                        {row.amended && <Chip data-help="commercial.lcList.amended" label="Amended" size="small" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell>{row.issuingBank}</TableCell>
                                    <TableCell>{row.buyerName}</TableCell>
                                    <TableCell align="right">{money(row.lcAmount)} {row.currencyCode}</TableCell>
                                    <TableCell align="right">{money(row.effectiveMaxValue)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.expiryDate)}</TableCell>
                                    <TableCell>
                                        <Chip data-help="commercial.lcList.status" size="small" label={enumLabel(row.status)} color={LC_STATUS_COLORS[row.status]} />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={row.dueReason ?? ''}>
                                            <Chip
                                                size="small"
                                                data-testid={`due-status-${row.id}`} data-help="commercial.lcList.due"
                                                label={row.dueReason ? `${enumLabel(row.dueStatus)} — ${row.dueReason}` : enumLabel(row.dueStatus)}
                                                color={DUE_STATUS_COLORS[row.dueStatus]}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center">No export LCs found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default ExportLcList;
